import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import FunRouletteWheel3D from "./FunRouletteWheel3D";
import computerAuthService from "../services/computerAuthService";
import authService from "../services/authService";
import gameService from "../services/gameService";
import { sounds } from "../utils/audio";
import "./FunRouletteGame.css";

// 8 Chip Denominations matching reference photo
const CHIP_VALUES = [1, 5, 10, 50, 100, 500, 1000, 5000];

// Standard American Roulette Numbers Order for 3 Rows x 12 Columns
const NUMBER_ROWS = [
  [3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36],
  [2, 5, 8, 11, 14, 17, 20, 23, 26, 29, 32, 35],
  [1, 4, 7, 10, 13, 16, 19, 22, 25, 28, 31, 34]
];

const RED_NUMBERS = new Set([
  1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36
]);

const getNumberColor = (num) => {
  if (num === 0 || num === "0" || num === "00") return "green";
  return RED_NUMBERS.has(Number(num)) ? "red" : "black";
};

// Ornate Baroque Filigree SVG for Score / Winner Header Boxes
const OrnateHeaderFlourish = ({ flip = false }) => (
  <svg
    width="120"
    height="48"
    viewBox="0 0 160 60"
    style={{
      transform: flip ? "scaleX(-1)" : "none",
      filter: "drop-shadow(0 2px 5px rgba(0,0,0,0.9))",
      pointerEvents: "none"
    }}
  >
    <defs>
      <linearGradient id="goldLuxuryGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#fff9d6" />
        <stop offset="25%" stopColor="#ffd700" />
        <stop offset="55%" stopColor="#d49e1e" />
        <stop offset="85%" stopColor="#9e6e08" />
        <stop offset="100%" stopColor="#4a3100" />
      </linearGradient>
    </defs>
    {/* Upper grand curving scroll */}
    <path
      d="M10,48 C25,20 50,8 90,12 C125,15 145,26 155,10 C148,28 130,38 95,30 C65,24 38,34 25,52 Z"
      fill="url(#goldLuxuryGrad)"
    />
    {/* Lower support leaf curl */}
    <path
      d="M5,42 C18,35 32,38 45,46 C32,48 20,52 8,58 C5,54 4,48 5,42 Z"
      fill="url(#goldLuxuryGrad)"
    />
    {/* Acanthus leaf curls */}
    <path
      d="M75,18 C90,6 115,8 135,16 C120,20 105,18 90,22 Z"
      fill="url(#goldLuxuryGrad)"
      opacity="0.85"
    />
    {/* Sparkle beads */}
    <circle cx="155" cy="10" r="3" fill="#ffffff" />
    <circle cx="8" cy="58" r="2" fill="#fff5b8" />
    <circle cx="95" cy="12" r="2" fill="#fff5b8" />
  </svg>
);

// Ornate Filigree Frame for the Center Title Banner
const OrnateTitleFrame = ({ children }) => (
  <div className="fr-title-frame-wrap">
    <svg
      className="fr-title-frame-svg"
      viewBox="0 0 340 52"
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id="titleBorderGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#664600" />
          <stop offset="15%" stopColor="#d49e1e" />
          <stop offset="50%" stopColor="#fff3b0" />
          <stop offset="85%" stopColor="#d49e1e" />
          <stop offset="100%" stopColor="#664600" />
        </linearGradient>
      </defs>
      {/* Outer baroque cartouche outline */}
      <path
        d="M25,8 Q45,2 80,4 Q170,0 260,4 Q295,2 315,8 Q335,16 338,26 Q335,36 315,44 Q295,50 260,48 Q170,52 80,48 Q45,50 25,44 Q5,36 2,26 Q5,16 25,8 Z"
        fill="rgba(10, 18, 12, 0.75)"
        stroke="url(#titleBorderGrad)"
        strokeWidth="2.5"
        filter="drop-shadow(0 3px 8px rgba(0,0,0,0.9))"
      />
      {/* Ornate corner leaves left */}
      <path
        d="M20,12 C10,18 8,34 20,40 C14,32 14,20 20,12 Z"
        fill="url(#titleBorderGrad)"
      />
      <circle cx="6" cy="26" r="3" fill="#fff5b8" />
      {/* Ornate corner leaves right */}
      <path
        d="M320,12 C330,18 332,34 320,40 C326,32 326,20 320,12 Z"
        fill="url(#titleBorderGrad)"
      />
      <circle cx="334" cy="26" r="3" fill="#fff5b8" />
      {/* Top central crown jewel */}
      <polygon points="170,1 176,6 170,11 164,6" fill="#fff8d1" />
    </svg>
    <div className="fr-title-content-layer">{children}</div>
  </div>
);

// SVG Faceted Diamond Gemstone Graphic
const DiamondGem = () => (
  <svg width="30" height="30" viewBox="0 0 100 100" className="fr-diamond-gem">
    <defs>
      <linearGradient id="gemGradTop" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="40%" stopColor="#d0eeff" />
        <stop offset="100%" stopColor="#7ec8fc" />
      </linearGradient>
      <linearGradient id="gemGradBot" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#5bb2f5" />
        <stop offset="100%" stopColor="#1e68bd" />
      </linearGradient>
    </defs>
    <polygon points="50,6 84,32 16,32" fill="url(#gemGradTop)" stroke="#ffffff" strokeWidth="1.5" />
    <polygon points="50,94 16,32 84,32" fill="url(#gemGradBot)" stroke="#ffffff" strokeWidth="1.5" />
    <polygon points="50,6 32,32 50,94" fill="rgba(255,255,255,0.5)" />
    <polygon points="50,6 68,32 50,94" fill="rgba(0,0,0,0.18)" />
    <polygon points="50,6 50,94" stroke="#ffffff" strokeWidth="1" />
  </svg>
);

// SVG Ornate Bottom Filigree Divider Bar
const GoldFooterBar = () => (
  <svg width="440" height="18" viewBox="0 0 500 24" style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.9))" }}>
    <defs>
      <linearGradient id="footerBarGrad" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#4a3100" />
        <stop offset="25%" stopColor="#d49e1e" />
        <stop offset="50%" stopColor="#fff3b0" />
        <stop offset="75%" stopColor="#d49e1e" />
        <stop offset="100%" stopColor="#4a3100" />
      </linearGradient>
    </defs>
    <path
      d="M250,12 Q200,4 120,12 Q40,18 5,12 C40,8 120,18 250,8 C380,18 460,8 495,12 Q460,18 380,12 Q300,4 250,12 Z"
      fill="url(#footerBarGrad)"
    />
    <circle cx="250" cy="10" r="4.5" fill="#ffffff" />
    <circle cx="120" cy="12" r="3" fill="#ffd700" />
    <circle cx="380" cy="12" r="3" fill="#ffd700" />
  </svg>
);

export default function FunRouletteGame() {
  const navigate = useNavigate();

  // Screen Auto-scaling state (1280x800 base cabinet dimensions)
  const [scale, setScale] = useState(1);

  // User & Account state
  const [computer, setComputer] = useState(() => computerAuthService.getComputer());
  const [balance, setBalance] = useState(() => {
    const comp = computerAuthService.getComputer();
    if (comp?.current_balance !== undefined && comp?.current_balance !== null && Number(comp.current_balance) > 0) {
      return Number(comp.current_balance);
    }
    const user = authService.getCurrentUser();
    if (user?.wallet !== undefined && Number(user.wallet) > 0) return Number(user.wallet);
    if (user?.coins !== undefined && Number(user.coins) > 0) return Number(user.coins);
    return 500.0;
  });

  // Betting & Table state
  const [selectedChip, setSelectedChip] = useState(10);
  const [placedBets, setPlacedBets] = useState({}); // key -> { type, value, amount }
  const [confirmedBets, setConfirmedBets] = useState({});
  const [totalBet, setTotalBet] = useState(0);

  // Game Round & Timer state (Synchronized with 1st game)
  const [currentRoundId, setCurrentRoundId] = useState(null);
  const [roundNo, setRoundNo] = useState(null);
  const [remainingSeconds, setRemainingSeconds] = useState(60);
  const [bettingOpen, setBettingOpen] = useState(true);

  // Wheel Animation state
  const [isWheelSpinning, setIsWheelSpinning] = useState(false);
  const [wheelTargetNumber, setWheelTargetNumber] = useState(null);
  const [settledWinningNumber, setSettledWinningNumber] = useState(null);
  const [wheelZoom, setWheelZoom] = useState(false);
  const [cancelSpecificMode, setCancelSpecificMode] = useState(false);

  // Result & Payout state
  const [winnerAmount, setWinnerAmount] = useState(0);
  const [recentHistory, setRecentHistory] = useState([5, 12, 8, 3, 9]);

  const displayedHistory = useMemo(() => {
    if (isWheelSpinning && wheelTargetNumber !== null && recentHistory.length > 0) {
      if (Number(recentHistory[0]) === Number(wheelTargetNumber)) {
        return recentHistory.slice(1, 6);
      }
    }
    return recentHistory.slice(0, 5);
  }, [isWheelSpinning, wheelTargetNumber, recentHistory]);

  // Modals state
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showTakeModal, setShowTakeModal] = useState(false);
  const [takeAmount, setTakeAmount] = useState("");
  const [isTaking, setIsTaking] = useState(false);

  // Round Refs
  const previousRoundIdRef = useRef(null);
  const localRemainingRef = useRef(60);
  const hasSpunThisRound = useRef(false);

  // 1. Responsive Viewport Scaling
  useEffect(() => {
    const handleResize = () => {
      const targetW = 1280;
      const targetH = 800;
      const s = Math.min(window.innerWidth / targetW, window.innerHeight / targetH);
      setScale(Math.max(0.65, Math.min(1.4, s)));
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 2. Fetch Real Last 5 Results from Database
  const fetchRecentResults = useCallback(async () => {
    try {
      const res = await gameService.getRecentResults(5);
      if (res?.success && Array.isArray(res.results)) {
        const nums = res.results
          .filter((r) => r.winning_number !== null && r.winning_number !== undefined)
          .map((r) => Number(r.winning_number))
          .slice(0, 5);
        if (nums.length > 0) {
          setRecentHistory(nums);
        }
      }
    } catch (err) {
      console.error("Failed to fetch recent results:", err);
    }
  }, []);

  // 3. Load Profile
  const loadProfile = useCallback(async () => {
    try {
      if (computerAuthService.isLoggedIn()) {
        const profile = await computerAuthService.getProfile();
        if (profile?.success && profile?.computer) {
          setComputer(profile.computer);
          const compBal = Number(profile.computer.current_balance ?? 0);
          setBalance(compBal > 0 ? compBal : 500.0);
        }
      } else {
        const user = authService.getCurrentUser();
        if (user) {
          const profile = await authService.getProfile();
          const bal = Number(profile?.user?.wallet ?? user.wallet ?? user.coins ?? 0);
          setBalance(bal > 0 ? bal : 500.0);
        }
      }
    } catch (err) {
      console.error("Profile load error:", err);
    }
  }, []);

  useEffect(() => {
    loadProfile();
    fetchRecentResults();
  }, [loadProfile, fetchRecentResults]);

  // 4. Synchronize with Backend Round (Exact same timer and betting window as 1st game)
  useEffect(() => {
    let active = true;

    const syncGameRound = async () => {
      try {
        const data = await gameService.getCurrentRound();
        if (!active) return;

        if (data?.success && data?.round) {
          const round = data.round;
          // Exact remaining seconds as 1st game (from 60 down to 0)
          const rem = Math.max(0, Number(round.remaining_seconds ?? 0));

          // Check if round ID changed (new round started)
          if (previousRoundIdRef.current !== null && Number(previousRoundIdRef.current) !== Number(round.id)) {
            // New round started on server!
            hasSpunThisRound.current = false;
            setIsWheelSpinning(false);
            setPlacedBets({});
            setConfirmedBets({});
            setTotalBet(0);
            setWinnerAmount(0);
            setSettledWinningNumber(null);
            loadProfile();
          }
          previousRoundIdRef.current = round.id;

          setCurrentRoundId(round.id);
          setRoundNo(round.round_no);
          setRemainingSeconds(rem);

          // Betting is open whenever round.betting_open is true (exact same as 1st game)
          const isBetOpen = Boolean(round.betting_open === true);
          setBettingOpen(isBetOpen);

          // Audio countdown warning in final seconds of betting (15s to 11s)
          if (rem <= 15 && rem > 10 && isBetOpen) {
            sounds.playCountdownSound(rem - 10);
          }

          // Trigger spin when entering SPINNING phase or last 10 seconds (exact same as 1st game)
          const isSpinPhase = (round.phase === "SPINNING" || !round.betting_open || rem <= 10);
          if (isSpinPhase && !hasSpunThisRound.current) {
            hasSpunThisRound.current = true;
            setBettingOpen(false);
            sounds.playNoMoreBetsSound();

            const target = (round.spin_target !== null && round.spin_target !== undefined)
              ? Number(round.spin_target)
              : Math.floor(Math.random() * 37);

            setWheelTargetNumber(target);
            setIsWheelSpinning(true);
          }

          return;
        }
      } catch (err) {
        // Backend scheduler might be idle or unavailable; run autonomous timer
      }

      // Autonomous 60-second fallback countdown (same as 1st game: 50s betting, 10s spin)
      if (localRemainingRef.current > 0) {
        localRemainingRef.current -= 1;
        setRemainingSeconds(localRemainingRef.current);

        if (localRemainingRef.current > 10) {
          setBettingOpen(true);
        } else {
          setBettingOpen(false);
        }

        if (localRemainingRef.current <= 15 && localRemainingRef.current > 10) {
          sounds.playCountdownSound(localRemainingRef.current - 10);
        }

        if (localRemainingRef.current <= 10 && !hasSpunThisRound.current) {
          hasSpunThisRound.current = true;
          setBettingOpen(false);
          sounds.playNoMoreBetsSound();

          const randomPick = Math.floor(Math.random() * 37);
          setWheelTargetNumber(randomPick);
          setIsWheelSpinning(true);
        }
      } else {
        if (!isWheelSpinning && !hasSpunThisRound.current) {
          localRemainingRef.current = 60;
          setRemainingSeconds(60);
          setBettingOpen(true);
        }
      }
    };

    const interval = setInterval(syncGameRound, 1000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [fetchRecentResults, loadProfile, isWheelSpinning]);

  // 5. Handle Wheel Settlement
  const handleWheelSettled = useCallback((winningNum) => {
    setIsWheelSpinning(false);
    setSettledWinningNumber(winningNum);
    sounds.playBallSettleSound();

    // Update Recent History only AFTER wheel stops!
    setRecentHistory((prev) => [winningNum, ...prev.filter((n) => n !== undefined && n !== winningNum)].slice(0, 5));
    fetchRecentResults();

    // Evaluate Winnings on placed/confirmed bets
    let wonPayout = 0;
    const num = Number(winningNum);
    const activeBetsToEval = Object.keys(confirmedBets).length > 0 ? confirmedBets : placedBets;

    Object.values(activeBetsToEval).forEach((bet) => {
      const amt = Number(bet.amount || 0);
      let isWin = false;
      let multiplier = 0;

      if (bet.type === "SINGLE") {
        isWin = (String(bet.value) === String(winningNum) || Number(bet.value) === num);
        multiplier = 35;
      } else if (bet.type === "COLUMN_1") {
        isWin = (num >= 1 && num <= 36 && num % 3 === 1);
        multiplier = 2;
      } else if (bet.type === "COLUMN_2") {
        isWin = (num >= 1 && num <= 36 && num % 3 === 2);
        multiplier = 2;
      } else if (bet.type === "COLUMN_3") {
        isWin = (num >= 1 && num <= 36 && num % 3 === 0);
        multiplier = 2;
      } else if (bet.type === "DOZEN_1") {
        isWin = (num >= 1 && num <= 12);
        multiplier = 2;
      } else if (bet.type === "DOZEN_2") {
        isWin = (num >= 13 && num <= 24);
        multiplier = 2;
      } else if (bet.type === "DOZEN_3") {
        isWin = (num >= 25 && num <= 36);
        multiplier = 2;
      } else if (bet.type === "RED") {
        isWin = RED_NUMBERS.has(num);
        multiplier = 1;
      } else if (bet.type === "BLACK") {
        isWin = (!RED_NUMBERS.has(num) && num > 0);
        multiplier = 1;
      } else if (bet.type === "EVEN") {
        isWin = (num > 0 && num % 2 === 0);
        multiplier = 1;
      } else if (bet.type === "ODD") {
        isWin = (num > 0 && num % 2 !== 0);
        multiplier = 1;
      } else if (bet.type === "LOW") {
        isWin = (num >= 1 && num <= 18);
        multiplier = 1;
      } else if (bet.type === "HIGH") {
        isWin = (num >= 19 && num <= 36);
        multiplier = 1;
      }

      if (isWin) {
        wonPayout += amt + amt * multiplier;
      }
    });

    setWinnerAmount(wonPayout);

    if (wonPayout > 0) {
      sounds.playWinSound();
      setBalance((b) => b + wonPayout);
      toast.success(`🎉 YOU WON ${wonPayout.toFixed(2)} COINS!`);
    }

    // Refresh history from backend to ensure synchronization
    fetchRecentResults();

    // Prepare next round after 5 seconds if running standalone fallback
    setTimeout(() => {
      setWinnerAmount(0);
      setSettledWinningNumber(null);
      if (!currentRoundId) {
        setPlacedBets({});
        setConfirmedBets({});
        setTotalBet(0);
        localRemainingRef.current = 60;
        hasSpunThisRound.current = false;
        setBettingOpen(true);
      }
    }, 5000);
  }, [confirmedBets, placedBets, currentRoundId, fetchRecentResults]);

  // 5. Placing Chips on Board
  const handlePlaceBet = (betType, betValue) => {
    const key = `${betType}:${betValue}`;

    // Cancel Specific Bet Mode: click any placed cell to remove bet & refund
    if (cancelSpecificMode) {
      if (placedBets[key]) {
        const refund = Number(placedBets[key].amount || 0);
        setPlacedBets((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
        setBalance((b) => b + refund);
        setTotalBet((t) => Math.max(0, t - refund));
        sounds.playChipSound();
        toast.info(`Canceled bet on ${betValue} (+${refund})`);
      } else {
        toast.info("No bet placed on this position to cancel.");
      }
      return;
    }

    if (!bettingOpen || isWheelSpinning) {
      toast.warning("Betting is currently closed!");
      return;
    }

    if (balance < selectedChip) {
      toast.error("Insufficient balance to place chip!");
      return;
    }
    const currentAmt = placedBets[key]?.amount || 0;
    const newAmt = currentAmt + selectedChip;

    setPlacedBets((prev) => ({
      ...prev,
      [key]: {
        type: betType,
        value: betValue,
        amount: newAmt
      }
    }));

    setBalance((b) => Math.max(0, b - selectedChip));
    setTotalBet((t) => t + selectedChip);
    sounds.playChipSound();
  };

  // 6. "Bet Ok" - Confirm Bets
  const handleBetOk = async () => {
    if (Object.keys(placedBets).length === 0) {
      toast.info("Place chips on the table first!");
      return;
    }

    setConfirmedBets({ ...placedBets });

    // Send to backend if active round exists
    if (currentRoundId) {
      try {
        const payload = {
          round_id: currentRoundId,
          bets: Object.values(placedBets).map((b) => ({
            bet_type: b.type,
            bet_value: String(b.value),
            amount: Number(b.amount)
          }))
        };
        const res = await gameService.placeBets(payload);
        if (res?.current_balance !== undefined) {
          setBalance(Number(res.current_balance));
        }
        toast.success("Bets placed successfully!");
      } catch (err) {
        console.warn("Backend bet submit notice:", err.message);
        toast.success("Bets locked for this round!");
      }
    } else {
      toast.success("Bets locked for this round!");
    }
  };

  // 7. "Cancel Bet" - Clear Unconfirmed Bets
  const handleCancelBet = () => {
    if (Object.keys(placedBets).length === 0) return;
    if (Object.keys(confirmedBets).length > 0) {
      toast.warning("Confirmed bets cannot be canceled for this round.");
      return;
    }

    // Refund chips back to balance
    setBalance((b) => b + totalBet);
    setPlacedBets({});
    setTotalBet(0);
    toast.info("Bets cleared.");
  };

  // 8. "Take" - Transfer Coins / Cashout
  const handleOpenTakeModal = () => {
    setTakeAmount(String(balance > 0 ? balance : 0));
    setShowTakeModal(true);
  };

  const handleConfirmTakeCoins = async () => {
    const amt = Number(takeAmount);
    if (!amt || amt <= 0 || amt > balance) {
      toast.error("Please enter a valid amount.");
      return;
    }

    setIsTaking(true);
    try {
      if (computerAuthService.isLoggedIn()) {
        const res = await computerAuthService.takeCoins(amt);
        if (res?.success) {
          toast.success(res.message || "Coins transferred to shop successfully!");
          setBalance((b) => Math.max(0, b - amt));
          setShowTakeModal(false);
        } else {
          toast.error(res?.message || "Failed to take coins.");
        }
      } else {
        toast.success(`Successfully cashed out ${amt} coins!`);
        setBalance((b) => Math.max(0, b - amt));
        setShowTakeModal(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to take coins.");
    } finally {
      setIsTaking(false);
    }
  };

  // 9. "Exit" - Navigate Back
  const handleExit = () => {
    navigate("/computer-dashboard");
  };

  return (
    <div className="fun-roulette-viewport">
      <div className="fun-roulette-stage">
        {/* ========================================================
            1. TOP HEADER (Score, "Fun Roulette" Crest, Winner, Info)
            ======================================================== */}
        <header className="fr-header">
          {/* Left: Score Box with Baroque Flourishes */}
          <div className="fr-score-container">
            <OrnateHeaderFlourish />
            <div className="fr-header-box">
              <span className="fr-script-label">Score</span>
              <div className="fr-digital-box">
                <span className="fr-digital-value">{balance.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Center: Title Cartouche & Diamond */}
          <div className="fr-title-crest">
            <OrnateTitleFrame>
              <span className="fr-title-text">Fun Roulette</span>
              <DiamondGem />
            </OrnateTitleFrame>
          </div>

          {/* Right: Winner Box with Baroque Flourishes & Info Button */}
          <div className="fr-winner-container">
            <div className="fr-header-box">
              <span className="fr-script-label">Winner</span>
              <div className="fr-digital-box">
                <span className="fr-digital-value">{winnerAmount}</span>
              </div>
            </div>
            <OrnateHeaderFlourish flip />

            <button
              className="fr-info-btn"
              onClick={() => setShowInfoModal(true)}
              title="Game Rules & Payouts"
            >
              i
            </button>
          </div>
        </header>

        {/* ========================================================
            2. CENTER SECTION: TIMER, CHIPS, 3D WHEEL, HISTORY, BUTTONS
            ======================================================== */}
        <div className="fr-center-row">
          {/* Left: Timer + Chips Grid + Cancel Specific Bet */}
          <div className="fr-left-column">
            {/* Countdown Timer Display (Synchronized with 1st Game) */}
            <div className="fr-timer-card">
              <span className={`fr-timer-digits ${remainingSeconds <= 10 ? "urgent" : ""}`}>
                {remainingSeconds >= 60 ? "1:00" : `0:${remainingSeconds.toString().padStart(2, "0")}`}
              </span>
            </div>

            {/* 2 Rows x 4 Chips Selector */}
            <div className="fr-chips-wrap">
              <div className="fr-chips-board">
                {CHIP_VALUES.map((val) => (
                  <div
                    key={val}
                    className={`fr-chip-item chip-${val} ${selectedChip === val ? "active" : ""}`}
                    onClick={() => setSelectedChip(val)}
                    title={`Chip ${val}`}
                  >
                    <div className="fr-chip-inner-ring">
                      <span className="fr-chip-label">{val}</span>
                    </div>
                  </div>
                ))}
              </div>
              {/* Green Oval Neon Ring Indicator matching reference photo */}
              <div className="fr-chip-neon-ring" />
            </div>

            {/* "Cancel Specific Bet" Button matching reference photo */}
            <button
              className={`fr-cancel-specific-btn ${cancelSpecificMode ? "active" : ""}`}
              onClick={() => {
                const nextMode = !cancelSpecificMode;
                setCancelSpecificMode(nextMode);
                if (nextMode) {
                  toast.info("Cancel Specific: Click any placed bet on the board to remove it.");
                } else {
                  toast.info("Normal Betting Mode resumed.");
                }
              }}
              title="Cancel Specific Bet"
            >
              Cancel Specific Bet
            </button>
          </div>

          {/* Center: 3D Roulette Wheel in Perspective */}
          <div className="fr-wheel-container">
            <FunRouletteWheel3D
              isSpinning={isWheelSpinning}
              winningNumber={wheelTargetNumber}
              spinDuration={4.0}
              variant="AMERICAN"
              wheelZoom={wheelZoom}
              onSettled={handleWheelSettled}
            />
          </div>

          {/* Right: Winning History, Wheel Zoom & Action Buttons */}
          <div className="fr-right-column">
            {/* History Bar */}
            <div className="fr-history-card">
              {displayedHistory.map((num, idx) => {
                const color = getNumberColor(num);
                return (
                  <span key={idx} className={`fr-history-num ${color}`}>
                    {num}
                  </span>
                );
              })}
            </div>

            {/* Wheel Zoom Toggle Button matching reference photo */}
            <button
              className={`fr-zoom-toggle-btn ${wheelZoom ? "active" : ""}`}
              onClick={() => setWheelZoom((z) => !z)}
              title="Toggle Wheel Zoom"
            >
              Wheel Zoom: {wheelZoom ? "ON" : "OFF"}
            </button>

            {/* Action Buttons: Take, Bet Ok, Cancel Bet */}
            <div className="fr-actions-group">
              <div className="fr-top-actions">
                <button
                  className="fr-golden-pill-btn"
                  onClick={handleOpenTakeModal}
                  title="Take Coins"
                >
                  Take
                </button>
                <button
                  className="fr-golden-pill-btn"
                  onClick={handleBetOk}
                  disabled={!bettingOpen || isWheelSpinning || Object.keys(placedBets).length === 0}
                  title="Confirm Bet"
                >
                  Bet Ok
                </button>
              </div>

              <button
                className="fr-cancel-btn"
                onClick={handleCancelBet}
                disabled={!bettingOpen || isWheelSpinning || Object.keys(placedBets).length === 0}
                title="Cancel Bets"
              >
                Cancel Bet
              </button>
            </div>
          </div>
        </div>

        {/* ========================================================
            3. LOWER SECTION: AUTHENTIC GREEN FELT BETTING TABLE
            ======================================================== */}
        <div className="fr-table-section">
          <div className="fr-table-grid">
            {/* Zeros Column on Left (Split 50/50 vertically across the 3 rows) */}
            <div className="fr-zeros-col">
              <div
                className={`fr-cell fr-cell-half ${settledWinningNumber === "00" ? "active-win" : ""}`}
                onClick={() => handlePlaceBet("SINGLE", "00")}
              >
                <div className="fr-num-badge green">
                  <span className="fr-badge-num-text">00</span>
                </div>
                {placedBets["SINGLE:00"] && (
                  <div className="fr-placed-chip">
                    <span className="fr-placed-chip-val">{placedBets["SINGLE:00"].amount}</span>
                  </div>
                )}
              </div>

              <div
                className={`fr-cell fr-cell-half ${settledWinningNumber === 0 ? "active-win" : ""}`}
                onClick={() => handlePlaceBet("SINGLE", 0)}
              >
                <div className="fr-num-badge green">
                  <span className="fr-badge-num-text">0</span>
                </div>
                {placedBets["SINGLE:0"] && (
                  <div className="fr-placed-chip">
                    <span className="fr-placed-chip-val">{placedBets["SINGLE:0"].amount}</span>
                  </div>
                )}
              </div>
            </div>

            {/* 36 Main Numbers Grid (Rows: 3-36, 2-35, 1-34) */}
            {NUMBER_ROWS.map((row, rowIdx) =>
              row.map((num, colIdx) => {
                const isRed = RED_NUMBERS.has(num);
                const isWin = settledWinningNumber === num;
                const betKey = `SINGLE:${num}`;
                const hasBet = placedBets[betKey];

                return (
                  <div
                    key={num}
                    className={`fr-cell ${isWin ? "active-win" : ""}`}
                    style={{
                      gridRow: `${rowIdx + 1} / ${rowIdx + 2}`,
                      gridColumn: `${colIdx + 2} / ${colIdx + 3}`
                    }}
                    onClick={() => handlePlaceBet("SINGLE", num)}
                  >
                    <div className={`fr-num-badge ${isRed ? "red" : "black"}`}>
                      <span className="fr-badge-num-text">{num}</span>
                    </div>

                    {hasBet && (
                      <div className="fr-placed-chip">
                        <span className="fr-placed-chip-val">{hasBet.amount}</span>
                      </div>
                    )}
                  </div>
                );
              })
            )}

            {/* "2 To 1" Column Bets on Right */}
            <div
              className="fr-cell fr-cell-col1"
              onClick={() => handlePlaceBet("COLUMN_1", "COLUMN_1")}
            >
              <span className="fr-col-text">2<br />To<br />1</span>
              {placedBets["COLUMN_1:COLUMN_1"] && (
                <div className="fr-placed-chip">
                  <span className="fr-placed-chip-val">{placedBets["COLUMN_1:COLUMN_1"].amount}</span>
                </div>
              )}
            </div>

            <div
              className="fr-cell fr-cell-col2"
              onClick={() => handlePlaceBet("COLUMN_2", "COLUMN_2")}
            >
              <span className="fr-col-text">2<br />To<br />1</span>
              {placedBets["COLUMN_2:COLUMN_2"] && (
                <div className="fr-placed-chip">
                  <span className="fr-placed-chip-val">{placedBets["COLUMN_2:COLUMN_2"].amount}</span>
                </div>
              )}
            </div>

            <div
              className="fr-cell fr-cell-col3"
              onClick={() => handlePlaceBet("COLUMN_3", "COLUMN_3")}
            >
              <span className="fr-col-text">2<br />To<br />1</span>
              {placedBets["COLUMN_3:COLUMN_3"] && (
                <div className="fr-placed-chip">
                  <span className="fr-placed-chip-val">{placedBets["COLUMN_3:COLUMN_3"].amount}</span>
                </div>
              )}
            </div>

            {/* Dozens Bets: 1st 12, 2nd 12, 3rd 12 */}
            <div
              className="fr-cell fr-cell-doz1"
              onClick={() => handlePlaceBet("DOZEN_1", "DOZEN_1")}
            >
              <span className="fr-dozen-text">1st 12</span>
              {placedBets["DOZEN_1:DOZEN_1"] && (
                <div className="fr-placed-chip">
                  <span className="fr-placed-chip-val">{placedBets["DOZEN_1:DOZEN_1"].amount}</span>
                </div>
              )}
            </div>

            <div
              className="fr-cell fr-cell-doz2"
              onClick={() => handlePlaceBet("DOZEN_2", "DOZEN_2")}
            >
              <span className="fr-dozen-text">2nd 12</span>
              {placedBets["DOZEN_2:DOZEN_2"] && (
                <div className="fr-placed-chip">
                  <span className="fr-placed-chip-val">{placedBets["DOZEN_2:DOZEN_2"].amount}</span>
                </div>
              )}
            </div>

            <div
              className="fr-cell fr-cell-doz3"
              onClick={() => handlePlaceBet("DOZEN_3", "DOZEN_3")}
            >
              <span className="fr-dozen-text">3rd 12</span>
              {placedBets["DOZEN_3:DOZEN_3"] && (
                <div className="fr-placed-chip">
                  <span className="fr-placed-chip-val">{placedBets["DOZEN_3:DOZEN_3"].amount}</span>
                </div>
              )}
            </div>

            {/* Outside Bets: 1 to 18, Even, Red, Black, Odd, 19 to 36 */}
            <div
              className="fr-cell fr-cell-low"
              onClick={() => handlePlaceBet("LOW", "LOW")}
            >
              <span className="fr-outside-text">1 To 18</span>
              {placedBets["LOW:LOW"] && (
                <div className="fr-placed-chip">
                  <span className="fr-placed-chip-val">{placedBets["LOW:LOW"].amount}</span>
                </div>
              )}
            </div>

            <div
              className="fr-cell fr-cell-even"
              onClick={() => handlePlaceBet("EVEN", "EVEN")}
            >
              <span className="fr-outside-text">Even</span>
              {placedBets["EVEN:EVEN"] && (
                <div className="fr-placed-chip">
                  <span className="fr-placed-chip-val">{placedBets["EVEN:EVEN"].amount}</span>
                </div>
              )}
            </div>

            <div
              className="fr-cell fr-cell-red"
              onClick={() => handlePlaceBet("RED", "RED")}
            >
              <div className="fr-diamond-badge red" />
              {placedBets["RED:RED"] && (
                <div className="fr-placed-chip">
                  <span className="fr-placed-chip-val">{placedBets["RED:RED"].amount}</span>
                </div>
              )}
            </div>

            <div
              className="fr-cell fr-cell-black"
              onClick={() => handlePlaceBet("BLACK", "BLACK")}
            >
              <div className="fr-diamond-badge black" />
              {placedBets["BLACK:BLACK"] && (
                <div className="fr-placed-chip">
                  <span className="fr-placed-chip-val">{placedBets["BLACK:BLACK"].amount}</span>
                </div>
              )}
            </div>

            <div
              className="fr-cell fr-cell-odd"
              onClick={() => handlePlaceBet("ODD", "ODD")}
            >
              <span className="fr-outside-text">Odd</span>
              {placedBets["ODD:ODD"] && (
                <div className="fr-placed-chip">
                  <span className="fr-placed-chip-val">{placedBets["ODD:ODD"].amount}</span>
                </div>
              )}
            </div>

            <div
              className="fr-cell fr-cell-high"
              onClick={() => handlePlaceBet("HIGH", "HIGH")}
            >
              <span className="fr-outside-text">19 To 36</span>
              {placedBets["HIGH:HIGH"] && (
                <div className="fr-placed-chip">
                  <span className="fr-placed-chip-val">{placedBets["HIGH:HIGH"].amount}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ========================================================
            4. BOTTOM FOOTER BAR (Total Bet, Gold Filigree Bar, Exit)
            ======================================================== */}
        <footer className="fr-footer">
          {/* Bottom Left: Total Bet Display with Ornate Curved Flourish */}
          <div className="fr-total-bet-box">
            <svg width="60" height="26" viewBox="0 0 80 30" style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.85))" }}>
              <path d="M5,22 Q25,8 50,14 T75,8 C70,18 55,22 40,18 Q25,14 12,26 Z" fill="url(#goldLuxuryGrad)" />
              <circle cx="76" cy="8" r="2.5" fill="#fff5b8" />
            </svg>
            <div className="fr-footer-display">
              <span className="fr-digital-value">{totalBet}</span>
            </div>
          </div>

          {/* Bottom Center: Ornate Game Status Banner matching Reference Image */}
          <div className="fr-footer-status-wrap">
            <svg width="45" height="30" viewBox="0 0 60 40" style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.85))" }}>
              <path d="M5,35 Q18,12 40,15 T55,8 C48,22 35,28 20,24 Q12,22 5,35 Z" fill="url(#goldLuxuryGrad)" />
            </svg>
            <div className="fr-status-capsule">
              <span className="fr-status-text">
                {isWheelSpinning
                  ? "Wheel Spinning..."
                  : bettingOpen
                  ? "Please Place Your Bets..."
                  : "Bet Time Over."}
              </span>
            </div>
            <svg width="45" height="30" viewBox="0 0 60 40" style={{ transform: "scaleX(-1)", filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.85))" }}>
              <path d="M5,35 Q18,12 40,15 T55,8 C48,22 35,28 20,24 Q12,22 5,35 Z" fill="url(#goldLuxuryGrad)" />
            </svg>
          </div>

          {/* Bottom Right: Exit Button with Ornate Curved Flourish */}
          <div className="fr-exit-box-wrap">
            <button className="fr-exit-btn" onClick={handleExit}>
              Exit
            </button>
            <svg width="60" height="26" viewBox="0 0 80 30" style={{ transform: "scaleX(-1)", filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.85))" }}>
              <path d="M5,22 Q25,8 50,14 T75,8 C70,18 55,22 40,18 Q25,14 12,26 Z" fill="url(#goldLuxuryGrad)" />
              <circle cx="76" cy="8" r="2.5" fill="#fff5b8" />
            </svg>
          </div>
        </footer>

        {/* ========================================================
            5. MODALS: GAME RULES & TAKE COINS
            ======================================================== */}
        {showInfoModal && (
          <div className="fr-modal-backdrop" onClick={() => setShowInfoModal(false)}>
            <div className="fr-modal-card" onClick={(e) => e.stopPropagation()}>
              <button className="fr-modal-close" onClick={() => setShowInfoModal(false)}>
                ✕
              </button>
              <h3 className="fr-modal-title">Fun Roulette Rules & Payouts</h3>

              <div style={{ maxHeight: "360px", overflowY: "auto", fontSize: "14px", lineHeight: "1.6" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", color: "#ddd" }}>
                  <thead>
                    <tr style={{ borderBottom: "1.5px solid #d4a743", color: "#ffd700" }}>
                      <th style={{ padding: "6px 0", textAlign: "left" }}>Bet Type</th>
                      <th style={{ padding: "6px 0", textAlign: "right" }}>Payout Ratio</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                      <td style={{ padding: "8px 0" }}>Single Number (0, 00, 1-36)</td>
                      <td style={{ padding: "8px 0", textAlign: "right", color: "#38e07a", fontWeight: "bold" }}>35 to 1</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                      <td style={{ padding: "8px 0" }}>Column Bets (2 To 1)</td>
                      <td style={{ padding: "8px 0", textAlign: "right", color: "#38e07a", fontWeight: "bold" }}>2 to 1</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                      <td style={{ padding: "8px 0" }}>Dozens (1st 12, 2nd 12, 3rd 12)</td>
                      <td style={{ padding: "8px 0", textAlign: "right", color: "#38e07a", fontWeight: "bold" }}>2 to 1</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                      <td style={{ padding: "8px 0" }}>Red / Black</td>
                      <td style={{ padding: "8px 0", textAlign: "right", color: "#38e07a", fontWeight: "bold" }}>1 to 1</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                      <td style={{ padding: "8px 0" }}>Even / Odd</td>
                      <td style={{ padding: "8px 0", textAlign: "right", color: "#38e07a", fontWeight: "bold" }}>1 to 1</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "8px 0" }}>1 to 18 (Low) / 19 to 36 (High)</td>
                      <td style={{ padding: "8px 0", textAlign: "right", color: "#38e07a", fontWeight: "bold" }}>1 to 1</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {showTakeModal && (
          <div className="fr-modal-backdrop" onClick={() => !isTaking && setShowTakeModal(false)}>
            <div className="fr-modal-card" onClick={(e) => e.stopPropagation()}>
              <button
                className="fr-modal-close"
                onClick={() => !isTaking && setShowTakeModal(false)}
              >
                ✕
              </button>
              <h3 className="fr-modal-title">Transfer Coins to Shop</h3>

              <p style={{ textAlign: "center", color: "#aaa", fontSize: "14px", margin: "0 0 16px 0" }}>
                Available Balance: <strong style={{ color: "#ffd700" }}>{balance.toFixed(2)}</strong> coins
              </p>

              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <input
                  type="number"
                  placeholder="Enter amount to transfer"
                  value={takeAmount}
                  onChange={(e) => setTakeAmount(e.target.value)}
                  disabled={isTaking}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    borderRadius: "8px",
                    background: "rgba(0,0,0,0.6)",
                    border: "1.5px solid #d4a743",
                    color: "#ffffff",
                    fontSize: "16px",
                    fontWeight: "bold",
                    outline: "none"
                  }}
                />

                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    type="button"
                    onClick={() => setTakeAmount(String(balance))}
                    disabled={isTaking}
                    style={{
                      flex: 1,
                      padding: "8px",
                      borderRadius: "6px",
                      background: "rgba(226, 182, 71, 0.2)",
                      border: "1px solid #d4a743",
                      color: "#ffd700",
                      fontWeight: "bold",
                      cursor: "pointer"
                    }}
                  >
                    Take All
                  </button>
                </div>

                <button
                  type="button"
                  className="fr-golden-pill-btn"
                  onClick={handleConfirmTakeCoins}
                  disabled={isTaking || Number(takeAmount) <= 0 || Number(takeAmount) > balance}
                  style={{ marginTop: "8px", height: "42px" }}
                >
                  {isTaking ? "Processing..." : "Confirm Transfer"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
